// src/features/chat/panel/panelShared.tsx
// Shared bits for the entity chat panel family (PanelMessageList,
// PanelComposer, PanelThreadView, PanelAttachmentsView, ChatLightbox).

import type { ReactNode } from 'react';

/** The one ghost icon button used across the chat chrome: 28px, tonal hover. */
export const ghostIconBtn =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none';

/** Same URL auto-link rule as ChatDetailPage. */
export const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/g;

export function messageId(m: any): string {
  return m?.entity?.id ?? m?.id ?? '';
}

export function messageTs(m: any): number {
  const ts = m?.created_at;
  if (!ts) return 0;
  if (typeof ts === 'object' && 'seconds' in ts) return (ts as { seconds: number }).seconds * 1000;
  const d = new Date(ts as string | number);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

export function byOldest(a: any, b: any): number {
  return messageTs(a) - messageTs(b);
}

/** Auto-link http(s) URLs in plain message text; opens in a new tab. */
export function renderLinkedText(text: string): ReactNode[] {
  if (!text) return [];
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  let i = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > last) nodes.push(<span key={`s${i}`}>{text.slice(last, match.index)}</span>);
    const href = match[0];
    nodes.push(
      <a
        key={`u${i}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline underline-offset-2 hover:no-underline"
        onClick={(e) => e.stopPropagation()}
      >
        {href}
      </a>,
    );
    last = URL_RE.lastIndex;
    i++;
  }
  if (last < text.length) nodes.push(<span key="tail">{text.slice(last)}</span>);
  return nodes;
}

/** Every URL mentioned in a message, for the Links view. */
export function extractLinks(text: string): string[] {
  if (!text) return [];
  URL_RE.lastIndex = 0;
  return text.match(URL_RE) ?? [];
}

export function linkDomain(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
